import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { FlowjoCommons } from '@portal/portal-core';
import type { RootState } from 'reducers';
import { FjTooltip } from 'components/primitives';
import { SectionBox, SectionBoxFooter } from 'components/licenses/section-box';
import { SuperAdminActions } from 'ducks/super-client';
import { selectProductFeatures } from 'selectors/super-admin';

const { newUUID } = FlowjoCommons.StringUtils;

// The value carried by a ProductAccessFeature varies by feature `value_type`.
type FeatureValue = string | number | boolean | null;

// A configurable feature available for a product (from the product_features table).
interface ProductFeature {
  id: string;
  feature_name: string;
  value_type: string;
  start_date?: string | null;
  end_date?: string | null;
  updated_at?: string | null;
}

// A ProductAccessFeature: the license-level enablement/value for a ProductFeature.
interface ProductAccessFeature {
  id: string;
  productFeatureId: string;
  name: string;
  value: FeatureValue;
  valueType?: string;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

type EnabledFeatures = Record<string, ProductAccessFeature>;

/**
 * Overrides for the per-seat use of this component.
 *
 * All optional, so the license modal - which edits one product access and needs none of them -
 * renders exactly as before. The Manage Users modal supplies them to get tri-state, the
 * "clear override" affordance, and license gating.
 */
interface ProductFeaturesManagerProps {
  productName?: string;
  onChange: (features: EnabledFeatures) => void;
  enabledFeatures?: EnabledFeatures;
  availableFeatures?: ProductFeature[];
  fetchFeatures: (productName: string) => void;
  /** Section heading. Defaults to 'Product Features'. */
  title?: string;
  /** Render expanded from the start rather than collapsed. */
  defaultExpanded?: boolean;
  /**
   * Rendered on its own line beneath a feature's row. Used to explain a state the control
   * cannot show, such as a selection whose seats disagree.
   */
  rowNote?: (featureName: string) => React.ReactNode;
  /**
   * Rendered inside the section box, below the feature rows. Used by the per-seat consumer for
   * its own apply/cancel, so feature changes are saved independently of the rest of its modal.
   */
  footer?: React.ReactNode;
  /**
   * Resolved state for a feature across whatever the caller is editing. Without it the
   * component falls back to its own single-target enabled/disabled reading.
   */
  resolveState?: (featureName: string) => 'on' | 'off';
  /** Called instead of onChange when the caller owns the toggle semantics. */
  onToggleFeature?: (featureName: string, next: boolean) => void;
}

const FeatureNote = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  margin-top: 4px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.grid.units(1)} 0;
`;

const FeatureLabel = styled.label`
  margin-left: ${({ theme }) => theme.grid.units(1)};
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

type OptionalThemeColors = {
  success?: string;
  warning?: string;
  primary?: string;
};

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span<{ state: 'on' | 'off' }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: lowercase;
  min-width: 64px;
  text-align: center;
  background-color: ${({ state, theme }) =>
    state === 'on'
      ? (theme.colors as OptionalThemeColors).success || '#4caf50'
      : (theme.colors as OptionalThemeColors).warning || '#ff9800'};
  color: ${({ theme }) => theme.colors.white};
`;

const ToggleSwitch = styled.button<{ state: 'on' | 'off' }>`
  position: relative;
  width: 44px;
  height: 24px;
  flex: 0 0 auto;
  background-color: ${({ state, theme }) =>
    state === 'on' ? (theme.colors as OptionalThemeColors).success || '#4caf50' : '#ccc'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 0;

  &:hover:not(:disabled) {
    background-color: ${({ state }) => (state === 'on' ? '#45a049' : '#b3b3b3')};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => (theme.colors as OptionalThemeColors).primary || '#1976d2'};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ToggleSwitchSlider = styled.span<{ state: 'on' | 'off' }>`
  position: absolute;
  top: 2px;
  left: ${({ state }) => (state === 'on' ? '22px' : '2px')};
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ValueInput = styled.input`
  margin-left: ${({ theme }) => theme.grid.units(1)};
  padding: ${({ theme }) => theme.grid.units(0.5)};
  border: 1px solid ${({ theme }) => theme.colors.defaultGreyBorderColor};
  border-radius: 4px;
  font-size: 14px;
  width: 200px;

  &:disabled {
    background-color: ${({ theme }) => theme.colors.defaultGreyBorderColor};
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.grid.units(1)};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    /* primary is not a defined theme color; preserves prior runtime behavior (resolves to undefined) */
    color: ${({ theme }) => (theme.colors as unknown as Record<string, string | undefined>).primary};
  }
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  transition: transform 0.2s ease;
  transform: ${({ expanded }) => (expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  display: inline-block;
  font-size: 12px;
  margin-right: ${({ theme }) => theme.grid.units(1)};
`;

const NoFeaturesMessage = styled.div`
  color: ${({ theme }) => theme.colors.disabledText};
  font-style: italic;
  text-align: center;
  padding: ${({ theme }) => theme.grid.units(2)} 0;
`;

// Presentational component: receives availableFeatures + fetchFeatures as props so
// it can be tested in isolation. The connected default export below injects them.
export const ProductFeaturesManager = ({
  productName,
  onChange,
  enabledFeatures = {},
  availableFeatures = [],
  fetchFeatures,
  title = 'Product Features',
  defaultExpanded = false,
  rowNote,
  footer,
  resolveState,
  onToggleFeature,
}: ProductFeaturesManagerProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const fetchedProducts = useRef(new Set<string>()); // Track which products we've already fetched

  const fetchProductFeatures = (): void => {
    // Guard against duplicate fetches
    if (!productName) return;
    if (fetchedProducts.current.has(productName)) return;

    // Mark this product as fetched before dispatching
    fetchedProducts.current.add(productName);
    fetchFeatures(productName);
  };

  // componentDidMount + componentDidUpdate(productName changed): fetch on mount and
  // whenever the product changes; the fetchedProducts set dedupes repeat fetches.
  useEffect(() => {
    fetchProductFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName]);

  const isFeatureEnabled = (featureName: string): boolean => {
    if (!enabledFeatures) return false;
    // Check all features to see if any have this name and are enabled
    return Object.values(enabledFeatures).some(
      (feature) => feature.name === featureName && feature.enabled === true,
    );
  };

  const findExistingFeature = (
    productFeatureId: string,
  ): { id: string; feature: ProductAccessFeature } | null => {
    if (!enabledFeatures) return null;
    // Find existing ProductAccessFeature by productFeatureId
    const entries = Object.entries(enabledFeatures);
    const found = entries.find(([, feature]) => feature.productFeatureId === productFeatureId);
    if (found) {
      const [id, feature] = found;
      return { id, feature };
    }
    return null;
  };

  const handleFeatureToggle = (feature: ProductFeature): void => {
    const isCurrentlyEnabled = isFeatureEnabled(feature.feature_name);
    const existing = findExistingFeature(feature.id);

    // Set proper default value based on valueType
    let defaultValue: FeatureValue;
    switch (feature.value_type) {
      case 'boolean':
        defaultValue = !isCurrentlyEnabled;
        break;
      case 'number':
        defaultValue = 0;
        break;
      case 'date':
        defaultValue = null;
        break;
      case 'string':
      default:
        defaultValue = '';
        break;
    }

    // Create proper ProductAccessFeature object
    const productAccessFeature: ProductAccessFeature = {
      id: existing ? existing.id : newUUID(), // Reuse existing ID or create new
      productFeatureId: feature.id, // ID from product_features table
      name: feature.feature_name,
      value: existing ? existing.feature.value : defaultValue,
      valueType: feature.value_type,
      enabled: !isCurrentlyEnabled,
      createdAt: existing ? existing.feature.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: existing ? existing.feature.version : 1,
    };

    const updatedFeatures: EnabledFeatures = {
      ...enabledFeatures,
      [productAccessFeature.id]: productAccessFeature, // Key by ProductAccessFeature ID
    };

    onChange(updatedFeatures);
  };

  const handleValueChange = (feature: ProductFeature, newValue: string): void => {
    const existing = findExistingFeature(feature.id);

    if (!existing) return; // Should not happen - value inputs only show when enabled

    // Parse value based on type
    let parsedValue: FeatureValue = newValue;
    if (feature.value_type === 'number') {
      parsedValue = newValue === '' ? 0 : Number(newValue);
    }

    // Update the existing feature with new value
    const productAccessFeature: ProductAccessFeature = {
      ...existing.feature,
      value: parsedValue,
      updatedAt: new Date().toISOString(),
    };

    const updatedFeatures: EnabledFeatures = {
      ...enabledFeatures,
      [existing.id]: productAccessFeature,
    };

    onChange(updatedFeatures);
  };

  const getCurrentValue = (feature: ProductFeature): FeatureValue => {
    const existing = findExistingFeature(feature.id);
    if (!existing) return '';

    // For boolean type, the value is the checkbox itself
    if (feature.value_type === 'boolean') {
      return existing.feature.value;
    }

    return existing.feature.value !== null && existing.feature.value !== undefined
      ? existing.feature.value
      : '';
  };

  const toggleExpanded = (): void => setIsExpanded((prev) => !prev);

  const features = availableFeatures || [];

  return (
    <SectionBox>
      <SectionTitle onClick={toggleExpanded}>
        <div>
          <ExpandIcon expanded={isExpanded}>▶</ExpandIcon>
          {title}
        </div>
        <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
          {isExpanded ? 'Click to collapse' : 'Click to expand'}
        </span>
      </SectionTitle>

      {isExpanded && (
        <>
          {features.length === 0 && (
            <NoFeaturesMessage>
              No configurable features available for this product
            </NoFeaturesMessage>
          )}

          {features.length > 0 && (
            <>
              {features.map((feature) => {
                const name = feature.feature_name;
                // A caller-supplied resolver wins; otherwise fall back to this component's own
                // single-target reading of enabledFeatures.
                const state = resolveState ? resolveState(name) : undefined;
                const isEnabled = state ? state === 'on' : isFeatureEnabled(name);
                // Falls back to the binary reading when no caller-supplied resolver is present,
                // which is the license modal's case.
                const displayState: 'on' | 'off' = state || (isEnabled ? 'on' : 'off');
                const badgeLabel = displayState === 'on' ? 'enabled' : 'disabled';
                const currentValue = getCurrentValue(feature);
                // Determine input type for non-boolean features
                let inputType = 'text';
                if (feature.value_type === 'number') {
                  inputType = 'number';
                } else if (feature.value_type === 'date') {
                  inputType = 'date';
                }
                return (
                  <FeatureItem key={feature.id}>
                    <ToggleContainer>
                      <StatusBadge state={displayState} data-testid={`feature-status-${name}`}>
                        {badgeLabel}
                      </StatusBadge>
                      <ToggleSwitch
                        type="button"
                        state={displayState}
                        aria-label={name}
                        aria-pressed={displayState === 'on'}
                        data-testid={`feature-toggle-${name}`}
                        onClick={() =>
                          onToggleFeature
                            ? onToggleFeature(name, state ? state !== 'on' : !isEnabled)
                            : handleFeatureToggle(feature)
                        }
                      >
                        <ToggleSwitchSlider state={displayState} />
                      </ToggleSwitch>
                    </ToggleContainer>
                    <FeatureLabel htmlFor={`feature-${feature.id}`}>
                      <FjTooltip
                        Component={
                          <span>
                            Type: {feature.value_type}
                            {feature.start_date && (
                              <>
                                <br />
                                Start: {new Date(feature.start_date).toLocaleDateString()}
                              </>
                            )}
                            {feature.end_date && (
                              <>
                                <br />
                                End: {new Date(feature.end_date).toLocaleDateString()}
                              </>
                            )}
                            {feature.updated_at && (
                              <>
                                <br />
                                Updated: {new Date(feature.updated_at).toLocaleDateString()}
                              </>
                            )}
                          </span>
                        }
                      >
                        <span>{feature.feature_name}</span>
                      </FjTooltip>
                    </FeatureLabel>

                    {/* Show value input for enabled features (except boolean which uses checkbox) */}
                    {isEnabled && feature.value_type !== 'boolean' && (
                      <ValueInput
                        type={inputType}
                        value={currentValue as string | number}
                        onChange={(e) => handleValueChange(feature, e.target.value)}
                        placeholder={`Enter ${feature.value_type} value`}
                      />
                    )}

                    {rowNote?.(name) && <FeatureNote>{rowNote(name)}</FeatureNote>}
                  </FeatureItem>
                );
              })}
            </>
          )}

          {footer && <SectionBoxFooter>{footer}</SectionBoxFooter>}
        </>
      )}
    </SectionBox>
  );
};

// Connected default export: injects availableFeatures (selector, keyed by
// productName) and fetchFeatures (dispatch) via hooks.
const ConnectedProductFeaturesManager = (
  props: Omit<ProductFeaturesManagerProps, 'availableFeatures' | 'fetchFeatures'>,
): React.JSX.Element => {
  const dispatch = useDispatch();
  const availableFeatures = useSelector((state: RootState) =>
    props.productName
      ? (selectProductFeatures(state) as Record<string, ProductFeature[]>)[props.productName] || []
      : [],
  );
  const fetchFeatures = (productName: string): void => {
    dispatch(SuperAdminActions.fetchProductFeatures(productName));
  };

  return (
    <ProductFeaturesManager
      {...props}
      availableFeatures={availableFeatures}
      fetchFeatures={fetchFeatures}
    />
  );
};

export default ConnectedProductFeaturesManager;
